/**
 * Functionally same as forwardParser.ts but instead of pulling data from
 * indexedDB, this one uses the main MetaJSON to get the data from.
 * This prevents any calls to IndexedDB.
 */
import type { BulkAction } from "@certa/studio-core/src/types/bulkAction.types";
import type { Field } from "@certa/studio-core/src/types/field.types";
import type { Process } from "@certa/studio-core/src/types/process.types";
import type { BaseConstraint } from "@certa/studio/src/types/rawConstraints.types";
import type {
  FieldConstraints,
  ProcessConstraints,
  BulkAction as RawBulkAction,
  StepConstraints,
  SwimlaneConstraints
} from "@certa/studio/src/types/rawProcess.types";
import type { RuleData } from "@certa/studio/src/components/RulesUI/Constraints/constraints.types";

import isEmpty from "lodash-es/isEmpty";
import { FieldType } from "@certa/types/src/studio";
import { NON_FILTERABLE_FIELDS } from "@certa/studio/src/constants/field.constants";
import {
  ENV_REGEX,
  filePathsRegex
} from "@certa/studio/src/constants/regex.constants";
import {
  ComponentKey,
  componentKeyToLabelMap
} from "@certa/studio-core/src/constants/process.constants";
import { RuleType } from "@certa/studio-core/src/constants/rules.constants";
import { READONLY_STEPTYPES } from "@certa/studio/src/forms/constants/steps.constants";
import { cleanRawRules } from "@certa/studio/src/utils/ruleCleaning.utils";
import { serializeSync } from "@certa/studio/src/utils/serializeRulesSync";
import { ruleTypeData } from "@certa/studio/src/constants/rules.constants";
import { arrayToHashmap } from "@certa/common/utils/array";
import type {
  ZodField,
  ZodRawProcess,
  ZodStep,
  ZodSwimlane
} from "@certa/queries/schemas/studio/deployableJSON.schema";
import type {
  ZodFieldAppJSON,
  ZodStepAppJSON,
  ZodSwimlaneAppJSON
} from "@certa/queries/schemas/studio/appProcessJSON.schema";
import { forwardParseECA } from "./ecaForwardParser";

const documentFieldTypes = [
  FieldType.WORD_DOCUMENT,
  FieldType.DOWNLOAD_ATTACHMENT
];

const forwardParseConstraints = <T = BaseConstraint>({
  ruleType,
  rulesUUIDs,
  metaJSON,
  shouldIgnoreInvalidReferences,
  parentContext // to display intermittent error message
}: {
  rulesUUIDs?: string[];
  ruleType: RuleType;
  metaJSON: Process;
  shouldIgnoreInvalidReferences?: boolean;
  parentContext: {
    componentType: ComponentKey;
    componentTag: string;
    processName: string;
  };
}) => {
  if (!rulesUUIDs?.length) return undefined;
  const { componentType, componentTag, processName } = parentContext;

  // const serializedRules = ruleDBToRaw(rulesUUIDs, ruleType);
  const isConditionLessConstraint =
    ruleType === RuleType.DataReduction ||
    ruleType === RuleType.AttachmentInheritance;

  const isConditionOptionalConstraint = ruleType === RuleType.Notification;

  const rules: RuleData[] = [];
  rulesUUIDs.forEach(ruleId => {
    rules.push(metaJSON.rules[ruleId]);
  });

  const filteredRules: RuleData[] = rules.filter((rule, idx) => {
    const isRuleEmptyConditionalCheck = !shouldIgnoreInvalidReferences && !rule;
    if (isRuleEmptyConditionalCheck) {
      const locationVerbiage =
        componentType === ComponentKey.PROCESS
          ? `'${processName}'`
          : `'${componentTag}' of process '${processName}'`;

      throw new Error(
        `Undefined '${ruleTypeData[ruleType].label}' rule found in ${componentKeyToLabelMap[componentType].toLowerCase()} ${locationVerbiage}. Kindly remove the undefined rule by searching the component's tag. Alternatively, you can export and reimport the code on Studio for auto-sanitization.`
      );
    }
    return rule && rule.isEnabled !== false; // undefined == enabled as well (for backward compatibility for old metaJSONs where isEnabled is not present)
  });

  const filteredRulesMap = arrayToHashmap(filteredRules, "id");

  const serializedRules = serializeSync<T>({
    rulesMap: filteredRulesMap,
    conditionLess: isConditionLessConstraint,
    conditionOptional: isConditionOptionalConstraint,
    metaJSON
  });

  return cleanRawRules(serializedRules, ruleType);
};

type ForwardParseDefaultValueProps = {
  defaultValue: Field["defaultValue"];
  fieldType: string;
  envOrSysVariable?: string;
};

const forwardParseDefaultValue = ({
  defaultValue,
  fieldType,
  envOrSysVariable
}: ForwardParseDefaultValueProps): ZodField["default_value"] => {
  if (Array.isArray(defaultValue)) {
    return defaultValue.join("~"); // If array (multi_select/checkbox/grouped_checkbox) then combine values with ~ sign
  }
  if (ENV_REGEX.test(defaultValue || "")) {
    if (
      [FieldType.BUSINESS_UNIT, FieldType.REGION].includes(
        fieldType as FieldType
      )
    ) {
      return envOrSysVariable;
    }
    return defaultValue;
  }
  return defaultValue;
};

const forwardParseFields = ({
  fieldUUIDs,
  metaJSON,
  shouldIgnoreInvalidReferences
}: {
  fieldUUIDs: string[];
  metaJSON: Process;
  shouldIgnoreInvalidReferences?: boolean;
}) => {
  if (!fieldUUIDs?.length) return {};

  const fields: Field[] = [];
  fieldUUIDs.forEach(fieldId => {
    fields.push(metaJSON.fields[fieldId]);
  });

  const fieldsAppJSON: ZodFieldAppJSON[] = [];
  const rawFields: Record<string, ZodField> = {};
  const filePaths = [];

  for (const fieldIndex in fields) {
    const field = fields[fieldIndex];
    if (shouldIgnoreInvalidReferences && !field) continue;
    if (!field)
      throw new Error(`Invalid field reference: ${fieldUUIDs[fieldIndex]}`);

    const {
      id,
      tag,
      fieldType,
      attachmentUrl,
      attachmentInheritanceConstraints,
      attachmentInheritanceConstraintsV2,
      relatedWorkflowFileConstraintsV2: relatedWorkflowFileConstraintsV2Param,
      childWorkflowCreationConstraintsRaw,
      childWorkflowCreationConstraints: childWorkflowCreationConstraintsParam,
      visibilityConstraints: visibilityConstraintsParam,
      dataInheritanceConstraints,
      integrationPollingConstraints: integrationPollingConstraintsParam,
      integrationTriggerConstraints: integrationTriggerConstraintsParam,
      dataReductionConstraints,
      relatedWorkflowFieldConstraintsRaw,
      relatedWorkflowFieldConstraints,
      RAGConstraints,
      readOnlyFieldConstraints: readOnlyFieldConstraintsParam,
      body,
      dataJSONPath,
      defaultThreads,
      defaultValue,
      envOrSysVariable,
      disabled: isDisabled,
      externalTag,
      extIsRequired: isExtIsRequired,
      extra,
      helpText,
      hidden: isHidden,
      isEncrypted,
      isEnabled,
      isFilterable,
      isRequired,
      jmesPath,
      logOnChange: shouldLogOnChange,
      meta,
      options,
      parentJSONField,
      prasaranOnChange: hasPrasaranOnChange,
      regexError,
      regexValue,
      requiredOnStepSubmit: isRequiredOnStepSubmit,
      searchParamJson,
      size,
      sourceCSVJSON: {
        csvSource: csvSourceSourceCSVJSON,
        filterColumns: filterColumnsSourceCSVJSON,
        ...restSourceCSVJSONProps
      } = {},
      sourceMapping,
      visibleCWActivityLog: shouldVisibleCWActivityLog,
      workflowMapping,
      modelFieldName,
      statusIndicators,
      __cs_comment: csComment,
      __modifiedAt,
      __createdAt,
      __tagReadonly: isTagReadonly,
      ...restFieldProps
    } = field;

    // check for fieldtype, if attachment/word doc take path and add to filePaths array
    if (
      documentFieldTypes.includes(fieldType as FieldType) &&
      attachmentUrl !== undefined
    ) {
      filePaths.push(attachmentUrl);
    }

    const commonConstraintParserProps = {
      metaJSON,
      shouldIgnoreInvalidReferences,
      parentContext: {
        componentType: ComponentKey.FIELDS,
        componentTag: tag,
        processName: metaJSON.process.kind.name
      }
    };

    const attachmentConstraints = forwardParseConstraints<
      FieldConstraints["attachment_inheritance_constraints"][0]
    >({
      rulesUUIDs: attachmentInheritanceConstraints,
      ruleType: RuleType.AttachmentInheritance,
      ...commonConstraintParserProps
    });

    const attachmentConstraintsV2 = forwardParseConstraints<
      FieldConstraints["attachment_inheritance_constraints_v2"][0]
    >({
      rulesUUIDs: attachmentInheritanceConstraintsV2,
      ruleType: RuleType.AttachmentInheritanceV2,
      ...commonConstraintParserProps
    });

    const relatedWorkflowFileConstraintsV2 = forwardParseConstraints<
      FieldConstraints["related_workflow_file_constraints_v2"][0]
    >({
      rulesUUIDs: relatedWorkflowFileConstraintsV2Param,
      ruleType: RuleType.RelatedWorkflowFileConstraintsV2,
      ...commonConstraintParserProps
    });
    const childWorkflowCreationConstraints = !isEmpty(
      childWorkflowCreationConstraintsRaw
    )
      ? childWorkflowCreationConstraintsRaw
      : forwardParseConstraints<
          FieldConstraints["child_workflow_creation_constraints"][0]
        >({
          rulesUUIDs: childWorkflowCreationConstraintsParam,
          ruleType: RuleType.ChildProcessCreation,
          ...commonConstraintParserProps
        });

    const visibilityConstraints = forwardParseConstraints<
      FieldConstraints["constraints"][0]
    >({
      rulesUUIDs: visibilityConstraintsParam,
      ruleType: RuleType.FieldVisibility,
      ...commonConstraintParserProps
    });

    const inheritanceConstraints = forwardParseConstraints<
      FieldConstraints["inheritance_constraints_v2"][0]
    >({
      rulesUUIDs: dataInheritanceConstraints,
      ruleType: RuleType.DataInheritance,
      ...commonConstraintParserProps
    });

    const integrationPollingConstraints = forwardParseConstraints<
      FieldConstraints["integration_polling_constraints"][0]
    >({
      rulesUUIDs: integrationPollingConstraintsParam,
      ruleType: RuleType.IntegrationPolling,
      ...commonConstraintParserProps
    });

    const integrationTriggerConstraints = forwardParseConstraints<
      FieldConstraints["integration_trigger_constraints"][0]
    >({
      rulesUUIDs: integrationTriggerConstraintsParam,
      ruleType: RuleType.IntegrationTrigger,
      ...commonConstraintParserProps
    });

    const reductionConstraints = forwardParseConstraints<
      FieldConstraints["reduction_constraints"][0]
    >({
      rulesUUIDs: dataReductionConstraints,
      ruleType: RuleType.DataReduction,
      ...commonConstraintParserProps
    });

    const relatedProcessConstraints = !isEmpty(
      relatedWorkflowFieldConstraintsRaw
    )
      ? relatedWorkflowFieldConstraintsRaw
      : forwardParseConstraints<
          FieldConstraints["related_workflow_field_constraints"][0]
        >({
          rulesUUIDs: relatedWorkflowFieldConstraints,
          ruleType: RuleType.RelatedProcessDataInheritance,
          ...commonConstraintParserProps
        });

    const statusConstraints = forwardParseConstraints<
      FieldConstraints["status_constraints"][0]
    >({
      rulesUUIDs: RAGConstraints,
      ruleType: RuleType.RAG,
      ...commonConstraintParserProps
    });

    const readOnlyFieldConstraints = forwardParseConstraints<
      FieldConstraints["read_only_field_constraints"][0]
    >({
      rulesUUIDs: readOnlyFieldConstraintsParam,
      ruleType: RuleType.ReadOnlyFieldConstraints,
      ...commonConstraintParserProps
    });

    const fieldJSON: ZodField = {
      ...restFieldProps,
      // Constraints
      attachment_inheritance_constraints: attachmentConstraints,
      attachment_inheritance_constraints_v2: attachmentConstraintsV2,
      child_workflow_creation_constraints: childWorkflowCreationConstraints,
      constraints: visibilityConstraints,
      inheritance_constraints_v2: inheritanceConstraints,
      integration_polling_constraints: integrationPollingConstraints,
      integration_trigger_constraints: integrationTriggerConstraints,
      reduction_constraints: reductionConstraints,
      related_workflow_field_constraints: relatedProcessConstraints,
      related_workflow_file_constraints_v2: relatedWorkflowFileConstraintsV2,
      status_constraints: statusConstraints,
      read_only_field_constraints: readOnlyFieldConstraints,

      // Other props
      body,
      data_json_path: Array.isArray(dataJSONPath)
        ? dataJSONPath.filter(val => val).map(val => val.trim())
        : undefined,
      default_threads: defaultThreads,
      default_value: forwardParseDefaultValue({
        defaultValue,
        fieldType,
        envOrSysVariable
      }),
      disabled: isDisabled,
      external_tag: externalTag || undefined,
      ext_is_required: isExtIsRequired || undefined,
      extra,
      field_type: fieldType,
      help_text: helpText,
      hidden: isHidden,
      is_encrypted: isEncrypted,
      is_enabled: isEnabled,
      is_filterable:
        !NON_FILTERABLE_FIELDS.includes(fieldType) && isFilterable === undefined
          ? !isHidden
            ? true
            : false
          : isFilterable, // Ref: DS-101
      is_required: isRequired,
      jmes_path: jmesPath,
      log_on_change: shouldLogOnChange,
      meta: meta?.height ? { height: meta.height } : undefined,
      options,
      parent_json_field: parentJSONField,
      prasaran_on_change: hasPrasaranOnChange,
      regex_error: regexError,
      regex_value: regexValue,
      required_on_step_submit: isRequiredOnStepSubmit,
      search_param_json: searchParamJson?.length ? searchParamJson : undefined,
      size: size,
      source_csv_json:
        field.sourceCSVJSON && Object.values(field.sourceCSVJSON).length > 0
          ? {
              ...restSourceCSVJSONProps,
              csv_source: csvSourceSourceCSVJSON,
              filter_columns: filterColumnsSourceCSVJSON
            }
          : undefined,
      source_mapping: sourceMapping,
      attachment_url: attachmentUrl,
      tag: tag,
      visible_cw_activity_log: shouldVisibleCWActivityLog,
      workflow_mapping: workflowMapping,
      model_field_name: modelFieldName || undefined,
      status_indicators: statusIndicators,
      __cs_comment: csComment
    };

    rawFields[`${fieldIndex}`] = fieldJSON;
    fieldsAppJSON.push({
      ...fieldJSON,
      __studio_id: id,
      __studio_modified_at: __modifiedAt,
      __studio_created_at: __createdAt
    });
  }

  return { rawFields, fieldsAppJSON, fieldFilePaths: filePaths };
};

const forwardParseSteps = ({
  stepsUUIDs,
  metaJSON,
  shouldIgnoreInvalidReferences
}: {
  stepsUUIDs: string[];
  metaJSON: Process;
  shouldIgnoreInvalidReferences?: boolean;
}) => {
  if (!stepsUUIDs?.length) return {};

  const steps = stepsUUIDs.map(stepId => metaJSON.steps[stepId]);

  const rawSteps: Record<string, ZodStep> = {};
  const rawStepsAppJSON: ZodStepAppJSON[] = [];
  let filePaths: string[] = [];
  for (const stepIndex in steps) {
    const step = steps[stepIndex];
    if (shouldIgnoreInvalidReferences && !step) continue;
    if (!step)
      throw new Error(`Invalid field reference: ${stepsUUIDs[stepIndex]}`);

    const {
      id,
      fields,
      tag,
      availableUserTags,
      accessGroupConstraints: accessGroupConstraintsRaw,
      stepActionConstraints,
      visibilityConstraints: visibilityConstraintsRaw,
      pdfConstraints: pdfConstraintsRaw,
      notifications,
      notificationsRaw,
      versionNamingConstraints: versionNamingConstraintsRaw,
      defaultState: {
        isEnabled: isEnabledDefaultState,
        isLocked: isLockedDefaultState,
        ...restDefaultState
      } = {},
      defaultThreads,
      dependentSteps,
      extra,
      dynamicGroupNames,
      forceIntegrationCall,
      myTasksGroups,
      name,
      pdfConfig,
      staticGroups,
      stepType,
      taggableDynamicGroupNames,
      taggableUserGroups,
      visibleCWActivityLog: shouldVisibleCWActivityLog,
      externalTag,
      collectionWorkflowConfig,
      deadline,
      deadlineMins,
      __cs_comment: csComment,
      __modifiedAt,
      __createdAt,
      __tagReadonly: isTagReadonly,
      isFilterable,
      ...rest
    } = step;
    const {
      fieldsAppJSON = [],
      rawFields = {},
      fieldFilePaths = []
    } = forwardParseFields({
      fieldUUIDs: fields,
      metaJSON,
      shouldIgnoreInvalidReferences
    });
    filePaths = [...filePaths, ...fieldFilePaths];

    const commonConstraintParserProps = {
      metaJSON,
      shouldIgnoreInvalidReferences,
      parentContext: {
        componentType: ComponentKey.STEPS,
        componentTag: tag,
        processName: metaJSON.process.kind.name
      }
    };

    const accessGroupConstraints = forwardParseConstraints<
      StepConstraints["access_group_constraints"][0]
    >({
      rulesUUIDs: accessGroupConstraintsRaw,
      ruleType: RuleType.StepAccess,
      ...commonConstraintParserProps
    });

    const actionConstraints = forwardParseConstraints<
      StepConstraints["action_constraints_v2"][0]
    >({
      rulesUUIDs: stepActionConstraints,
      ruleType: RuleType.StepAction,
      ...commonConstraintParserProps
    });

    const visibilityConstraints = forwardParseConstraints<
      StepConstraints["constraints"][0]
    >({
      rulesUUIDs: visibilityConstraintsRaw,
      ruleType: RuleType.StepVisibility,
      ...commonConstraintParserProps
    });

    const pdfConstraints = forwardParseConstraints<
      StepConstraints["pdf_constraints"][0]
    >({
      rulesUUIDs: pdfConstraintsRaw,
      ruleType: RuleType.PDFGeneration,
      ...commonConstraintParserProps
    });

    const notificationConstraints = !isEmpty(notificationsRaw)
      ? notificationsRaw
      : forwardParseConstraints<StepConstraints["notifications"][0]>({
          rulesUUIDs: notifications,
          ruleType: RuleType.Notification,
          ...commonConstraintParserProps
        });

    const versionNamingConstraints = forwardParseConstraints<
      StepConstraints["version_naming_constraints_v2"][0]
    >({
      rulesUUIDs: versionNamingConstraintsRaw,
      ruleType: RuleType.VersionNamingConstraints,
      ...commonConstraintParserProps
    });

    rawSteps[`${stepIndex}`] = {
      ...rest,
      // Constraints
      access_group_constraints: accessGroupConstraints,
      action_constraints_v2: actionConstraints,
      constraints: visibilityConstraints,
      pdf_constraints: pdfConstraints,
      notifications: notificationConstraints,
      version_naming_constraints_v2: versionNamingConstraints,

      available_user_tags: availableUserTags,
      collection_workflow_config: collectionWorkflowConfig,
      deadline:
        (!stepType || (stepType && !READONLY_STEPTYPES.includes(stepType))) &&
        deadline
          ? Math.floor(deadline) !== 0
            ? Math.floor(deadline)
            : undefined
          : undefined,
      deadline_mins:
        (!stepType || (stepType && !READONLY_STEPTYPES.includes(stepType))) &&
        deadlineMins &&
        deadlineMins !== 0
          ? deadlineMins
          : undefined,
      default_threads: defaultThreads,
      dependent_steps: dependentSteps?.length
        ? dependentSteps.join(",")
        : undefined,
      extra: extra
        ? {
            ...extra,
            display_pdf_modal: extra.display_pdf_modal ? true : undefined
          }
        : undefined,
      fields: rawFields,
      dynamic_group_names: dynamicGroupNames,
      force_integration_call: forceIntegrationCall,
      is_filterable: isFilterable === undefined ? true : isFilterable, // Ref: DS-101
      my_tasks_groups: myTasksGroups?.length ? myTasksGroups : undefined,
      name: name,
      pdf_config: pdfConfig,
      static_groups: staticGroups || undefined,
      step_type: stepType,
      tag: tag,
      taggable_dynamic_group_names: taggableDynamicGroupNames,
      taggable_user_groups: taggableUserGroups || undefined,
      visible_cw_activity_log: shouldVisibleCWActivityLog,
      external_tag: externalTag || undefined,
      __cs_comment: csComment,
      default_state: !isEmpty(step.defaultState)
        ? {
            ...restDefaultState,
            is_enabled: isEnabledDefaultState,
            is_locked: isLockedDefaultState
          }
        : undefined
    };
    rawStepsAppJSON.push({
      ...rawSteps[`${stepIndex}`],
      fields: fieldsAppJSON,
      __studio_id: id,
      __studio_modified_at: __modifiedAt,
      __studio_created_at: __createdAt
    });
  }

  return { rawSteps, rawStepsAppJSON, stepFilePaths: filePaths };
};

const forwardParseSwimlanes = ({
  swimlaneUUIDs,
  metaJSON,
  shouldIgnoreInvalidReferences
}: {
  swimlaneUUIDs: string[];
  metaJSON: Process;
  shouldIgnoreInvalidReferences?: boolean;
}) => {
  if (!swimlaneUUIDs?.length) {
    return { stepgroups: {}, stepgroupsAppJSON: [], stepGroupFilePaths: [] };
  }

  const swimlanes = swimlaneUUIDs.map(
    swimlaneId => metaJSON.swimlanes[swimlaneId]
  );

  const stepgroups: Record<string, ZodSwimlane> = {};
  const stepgroupsAppJSON: ZodSwimlaneAppJSON[] = [];
  let filePaths: string[] = [];
  for (const swimlaneIndex in swimlanes) {
    const swimlane = swimlanes[swimlaneIndex];
    if (shouldIgnoreInvalidReferences && !swimlane) continue;
    if (!swimlane)
      throw new Error(
        `Invalid field reference: ${swimlaneUUIDs[swimlaneIndex]}`
      );

    const {
      id,
      steps,
      dynamicGroupNames,
      myTaskGroups,
      name,
      staticGroups,
      tag,
      externalTag,
      __cs_comment: csComment,
      __modifiedAt,
      __createdAt,
      // not part of deployable JSON
      accessGroupConstraints: _,
      __tagReadonly: isTagReadonly,
      ...rest
    } = swimlane;

    const {
      rawSteps = {},
      rawStepsAppJSON = [],
      stepFilePaths = []
    } = forwardParseSteps({
      stepsUUIDs: steps,
      metaJSON,
      shouldIgnoreInvalidReferences
    });
    filePaths = [...filePaths, ...stepFilePaths];

    const accessGroupConstraints = forwardParseConstraints<
      SwimlaneConstraints["access_group_constraints"][0]
    >({
      rulesUUIDs: swimlane.accessGroupConstraints,
      ruleType: RuleType.StepGroupAccess,
      metaJSON,
      shouldIgnoreInvalidReferences,
      parentContext: {
        componentType: ComponentKey.SWIMLANES,
        componentTag: tag,
        processName: metaJSON.process.kind.name
      }
    });

    stepgroups[`${swimlaneIndex}`] = {
      ...rest,
      access_group_constraints: accessGroupConstraints,
      dynamic_group_names: dynamicGroupNames,
      my_tasks_groups: myTaskGroups?.length ? myTaskGroups : undefined,
      name,
      static_groups: staticGroups,
      steps: rawSteps,
      tag,
      external_tag: externalTag || undefined,
      __cs_comment: csComment
    };
    stepgroupsAppJSON.push({
      ...stepgroups[`${swimlaneIndex}`],
      steps: rawStepsAppJSON,
      __studio_id: id,
      __studio_modified_at: __modifiedAt,
      __studio_created_at: __createdAt
    });
  }

  return { stepgroups, stepgroupsAppJSON, stepGroupFilePaths: filePaths };
};

const forwardParseBulkActions = ({
  bulkActionUUIDs,
  metaJSON
}: {
  bulkActionUUIDs: string[];
  metaJSON: Process;
}) => {
  if (!bulkActionUUIDs?.length) return [];

  const bulkActions: BulkAction[] = [];
  bulkActionUUIDs.forEach(bulkActionId => {
    bulkActions.push(metaJSON.bulkActions[bulkActionId]);
  });

  const rawBulkActions: RawBulkAction[] = bulkActions.map(bulkAction => {
    const {
      actionTag,
      actionType,
      stepTag,
      enableSubmission: isEnableSubmission,
      enableUndoSubmission: isEnableUndoSubmission,
      editFields,
      otherFields,
      differentFields,
      contentType,
      editFieldDefTag,
      enableAdjudication: isEnableAdjudication,
      enableStatusUpdate: isEnableStatusUpdate,
      enablePublicComment: isEnablePublicComment,
      campaignId,
      name,
      // skipping below properties
      id,
      __modifiedAt,
      __createdAt,
      // rest should be passed as is
      ...rest
    } = bulkAction;

    return {
      ...rest,
      action_tag: actionTag,
      action_type: actionType,
      step_tag: stepTag,
      enable_submission: isEnableSubmission,
      enable_undo_submission: isEnableUndoSubmission,
      edit_fields: editFields,
      other_fields: otherFields,
      different_fields: differentFields,
      content_type: contentType,
      edit_field_def_tag: editFieldDefTag,
      enable_adjudication: isEnableAdjudication,
      enable_status_update: isEnableStatusUpdate,
      enable_public_comment: isEnablePublicComment,
      campaign_id: campaignId,
      name: name
    };
  });

  return rawBulkActions;
};

type DeployableProcessMetadata = Omit<
  ZodRawProcess,
  "stepgroups" | "event_rules" | "states" | "file_paths"
>;

/**
 *
 * @param metaJSON - The metaJSON to forward parse
 * @param shouldIgnoreInvalidReferences - Whether to ignore invalid references
 * @returns Deployable JSON without stepgroups, event_rules, states, and file_paths
 */

const forwardParseProcessMetadataSync = ({
  metaJSON,
  shouldIgnoreInvalidReferences
}: ForwardParseSyncProps): DeployableProcessMetadata => {
  const { process } = metaJSON;

  // TODO: change this when bulk actions are migrated from JSON
  // const bulkActions = await forwardParseBulkActions(process.bulkActions);
  const bulkActions =
    process.bulkActions && process.bulkActions.length > 0
      ? forwardParseBulkActions({
          bulkActionUUIDs: process.bulkActions,
          metaJSON
        })
      : process.bulkActionsRaw;

  const accessGroupConstraints = forwardParseConstraints<
    ProcessConstraints["access_group_constraints"][0]
  >({
    rulesUUIDs: process.accessGroupConstraints,
    ruleType: RuleType.WorkflowAccess,
    metaJSON,
    shouldIgnoreInvalidReferences,
    parentContext: {
      componentType: ComponentKey.PROCESS,
      componentTag: "",
      processName: metaJSON.process.kind.name
    }
  });

  const {
    id,
    kind: {
      availableStatuses,
      defaultStatus,
      name,
      tag,
      visibleToStaticGroups,
      permissions: {
        dynamicGroups: dynamicGroupsPermissions,
        staticGroups: staticGroupsPermissions,
        ...restKindPermissionsProps
      },
      objectTypes,
      ...restKindProps
    },
    defaultStepTags,
    defaultStepTagForGroups,
    staticGroups,
    dynamicGroupNames,
    dynamicGroupNamesWithPerm,
    creatorPermissions,
    mentionsTemplate,
    lcData,
    autoIntegrationCallFieldTags,
    onDeleteIntegrationFieldTags,
    nodeOrder,
    pdfConfigurations,
    relatedTypes,
    defaultThreads,
    modelReqFields,
    contentType,
    childMappings,
    onCreateWorkflow,
    __createdAt,
    __modifiedAt,
    __branchTimestamp,
    __saveId,
    __lastMigration,
    // not part of deployable JSON
    swimlaneSequence,
    eventRuleSequence,
    stateRuleSequence,
    bulkActions: _,
    bulkActionsRaw,
    ...restProcessProps
  } = process;

  const processJSON: DeployableProcessMetadata = {
    ...restProcessProps,
    access_group_constraints: accessGroupConstraints,
    auto_integration_call_field_tags: autoIntegrationCallFieldTags || undefined,
    bulk_actions: bulkActions,
    name,
    creator_permissions: Array.isArray(creatorPermissions)
      ? creatorPermissions
      : undefined,
    default_steps_for_groups:
      defaultStepTagForGroups && Object.keys(defaultStepTagForGroups).length
        ? defaultStepTagForGroups
        : undefined,
    default_step_tags: defaultStepTags || undefined,
    default_threads: defaultThreads,
    dynamic_group_names: dynamicGroupNames,
    dynamic_group_names_with_perm: dynamicGroupNamesWithPerm,
    extra_fields_json: Array.isArray(lcData)
      ? lcData.map(lcData => {
          const { displayType, label, format, ...restLcData } = lcData;
          return {
            ...restLcData,
            display_type:
              displayType ??
              /**
               * lcData is currently a JSON Editor field. So the user will likely use display_type instead of displayType
               * as they would expect the format to be like the deployable json. Can be removed once this has a proper UI.
               */
              // @ts-expect-error TS5 upgrade
              lcData.display_type,
            label,
            ...(format ? { format } : {})
          };
        })
      : undefined,
    mentions_template: mentionsTemplate || "mentions",
    node_order: Array.isArray(nodeOrder) ? nodeOrder : undefined,
    on_delete_integration_field_tags: onDeleteIntegrationFieldTags || undefined,
    pdf_configurations:
      Array.isArray(pdfConfigurations) && pdfConfigurations.length
        ? pdfConfigurations
        : undefined,
    related_types: relatedTypes,
    static_groups: staticGroups,
    model_req_fields:
      Array.isArray(modelReqFields) && modelReqFields.length
        ? modelReqFields
        : undefined,
    content_type: contentType || undefined,
    kind: {
      ...restKindProps,
      available_statuses: availableStatuses,
      default_status: defaultStatus,
      name,
      tag,
      visible_to_static_groups: visibleToStaticGroups,
      permissions: {
        ...restKindPermissionsProps,
        dynamic_groups: dynamicGroupsPermissions || {},
        static_groups: staticGroupsPermissions || {}
      },
      object_types: objectTypes
    },
    child_mappings: childMappings,
    on_create_workflow: onCreateWorkflow
  };

  return processJSON;
};

type ForwardParseSyncProps = {
  metaJSON: Process;
  shouldIgnoreInvalidReferences?: boolean;
};

/**
 *
 * @param processId
 * @returns
 */
export const forwardParseSync = ({
  metaJSON,
  shouldIgnoreInvalidReferences
}: ForwardParseSyncProps): ZodRawProcess => {
  try {
    const { process } = metaJSON;

    const processProps = forwardParseProcessMetadataSync({
      metaJSON,
      shouldIgnoreInvalidReferences
    });

    const { stepgroups = {}, stepGroupFilePaths = [] } = forwardParseSwimlanes({
      swimlaneUUIDs: process.swimlaneSequence,
      metaJSON,
      shouldIgnoreInvalidReferences
    });

    const filteredFilePaths = Array.from(
      stepGroupFilePaths.filter(path => path.match(filePathsRegex))
    );

    let ecaData = {};
    const eventRules = Object.values(metaJSON.eventRules || {});
    const stateRules = Object.values(metaJSON.stateRules || {});

    if (eventRules?.length || stateRules?.length) {
      ecaData = forwardParseECA(metaJSON, true);
    }

    const processJSON: ZodRawProcess = {
      ...processProps,
      stepgroups,
      file_paths: filteredFilePaths.length ? filteredFilePaths : undefined,
      ...ecaData
    };

    return processJSON;
  } catch (e: unknown) {
    throw e;
  }
};

export {
  forwardParseProcessMetadataSync,
  forwardParseSwimlanes,
  forwardParseDefaultValue
};
